import { type ChangeEvent, memo, useEffect, useMemo, useState } from 'react';

import { LocationCount, MapView } from '@virginexperiencedays/components-v2/src/layout/Maps';
import { LocationsList } from '@virginexperiencedays/components-v2/src/lists/Locations';
import type { Regions } from '@virginexperiencedays/components-v2/src/pages/product/Locations';
import { useWindowWidth } from '@virginexperiencedays/hooks';
import type { Location } from '@virginexperiencedays/products';
import { cn } from '@virginexperiencedays/tailwind';

import { useLocations } from '../hooks/useLocations';
import {
  RegionLocationOptions,
  RenderMap,
  RegionListDrawer,
  LocationsItinerary,
  type LocationsItineraryProps,
} from '../Locations';
import {
  RegionListDrawerTrigger,
  type RegionListDrawerTriggerProps,
} from '../Locations/RegionListDrawer';

export type LocationsWidgetProps = Pick<LocationsItineraryProps, 'locationsTitles'> & {
  gmapApiKey: string;
  hasDropdown?: boolean;
  hasItinerary?: boolean;
  locations: Location[];
  onClickMap?: (data: { text: string }) => void;
  onClickLocationSelect?: (data: { text: string }) => void;
  onSelectLocation?: (data: { text: string }) => void;
};

const DISTANCES_MAXIMUM_ZOOM = 14;

export const LocationsWidget = memo(
  ({
    hasItinerary,
    locations,
    locationsTitles,
    gmapApiKey,
    hasDropdown,
    onClickMap,
    onClickLocationSelect,
    onSelectLocation,
  }: LocationsWidgetProps) => {
    const {
      mapContainerRef,
      mapState: { isOpenModal, activeRegion, isStatic },
      handleStateChange,
      handleOpenChange,
      handleRegionClick,
      width,
      containerHeight,
      regions,
      regionNames,
      locationsArray,
      staticImageUrl,
    } = useLocations(locations, gmapApiKey);
    const { isDesktop } = useWindowWidth();

    const selectedLocation = useMemo(() => {
      const regionCount = regions?.[activeRegion]?.length || 0;

      return activeRegion && regions && Array.isArray(regions?.[activeRegion])
        ? `${activeRegion} (${regionCount} ${pluralizeLocation(regionCount)})`
        : 'All Locations';
    }, [activeRegion, regions]);

    useEffect(() => {
      if (!hasDropdown && isDesktop()) {
        handleStateChange('isOpenModal', false);
      }
    }, [hasDropdown, isDesktop()]);

    if (!Object.keys(regions).length || !locations.length) return null;

    const onClickOption = (name: string) => {
      handleRegionClick(name);
      handleStateChange('isStatic', true);

      onSelectLocation?.({ text: name });
    };

    const onClickStaticMap = () => {
      handleStateChange('isStatic', !isStatic);

      onClickMap?.({ text: selectedLocation });
    };

    const onClickAllLocation = () => {
      handleStateChange('activeRegion', '');
      handleStateChange('isOpenModal', false);
      handleStateChange('isStatic', true);
      onSelectLocation?.({ text: 'All Locations' });
    };

    return (
      <>
        <LocationCount
          locationCount={locations.length}
          locationName={locations[0]?.name}
          regionName={hasItinerary && regionNames?.length ? regionNames[0] : undefined}
        />

        <div
          className={cn(
            'mt-4 flex flex-col gap-4',
            !hasDropdown && 'md:flex-row lg:flex-col xl:flex-row'
          )}
        >
          {locations.length > 1 && (
            <>
              {hasDropdown && (
                <LocationsDropdown
                  onLocationSelect={() => {
                    onClickLocationSelect?.({ text: selectedLocation });
                  }}
                  onClickAllLocation={onClickAllLocation}
                  onClickOption={onClickOption}
                  regions={regions}
                  regionNames={regionNames}
                  selectedLocation={selectedLocation}
                />
              )}

              {hasItinerary && (
                <LocationsItinerary
                  className="order-2 mt-2 px-2 md:order-none md:mt-0 md:w-[320px] lg:order-2 lg:mt-2 lg:w-full xl:order-none xl:mt-0 xl:w-[354px]"
                  locations={locations}
                  locationsTitles={locationsTitles}
                />
              )}

              {/* default */}
              {!hasDropdown && !hasItinerary && (
                <>
                  {/* Desktop: Sidebar */}
                  <RegionLocationOptions
                    className="hidden max-h-[400px] overflow-y-auto overflow-x-hidden mega-nav-scrollbar md:block md:w-[320px] md:min-w-[320px] lg:max-h-[200px] lg:w-full xl:max-h-[400px] xl:w-[320px]"
                    regions={regions}
                    regionNames={regionNames}
                    activeRegion={activeRegion}
                    onClickAllLocation={onClickAllLocation}
                    onClickOption={onClickOption}
                  />

                  {/* Mobile: Drawer */}
                  <RegionListDrawerTrigger
                    className="md:hidden"
                    onClick={() => {
                      handleStateChange('isOpenModal', true);

                      onClickLocationSelect?.({ text: selectedLocation });
                    }}
                  >
                    Showing: <span className="ml-0.5 font-normal">{selectedLocation}</span>
                  </RegionListDrawerTrigger>

                  <RegionListDrawer
                    isOpenModal={isOpenModal}
                    activeRegion={activeRegion}
                    regions={regions}
                    regionNames={regionNames}
                    locations={locations}
                    handleOpenChange={handleOpenChange}
                    onClickOption={onClickOption}
                    onClickAllLocation={onClickAllLocation}
                  />
                </>
              )}
            </>
          )}

          {/* Actual Map */}
          <MapView ref={mapContainerRef}>
            <RenderMap
              locations={locationsArray}
              isStatic={isStatic}
              gmapApiKey={gmapApiKey}
              width={width}
              containerHeight={containerHeight}
              staticImageUrl={staticImageUrl}
              onClickStaticMap={onClickStaticMap}
              zoom={hasItinerary ? DISTANCES_MAXIMUM_ZOOM : undefined}
            />
          </MapView>
        </div>

        {!hasItinerary && <MapFootNote activeRegion={activeRegion} regions={regions} />}
      </>
    );
  }
);
LocationsWidget.displayName = 'LocationsWidget';

type MapFootNoteProps = {
  activeRegion: string;
  regions: Regions;
};

const MapFootNote = ({ activeRegion, regions }: MapFootNoteProps) => {
  const [isViewList, setIsViewList] = useState(false);

  useEffect(() => {
    if (!activeRegion) {
      setIsViewList(false);
    }
  }, [activeRegion]);

  return (
    <>
      <div className="mt-4">
        <p className="flex flex-nowrap justify-end gap-2 text-right font-inter text-xs text-grey-400">
          Locations shown shouldn&apos;t be used for route planning
          {activeRegion && (
            <span className="hidden gap-2 md:flex">
              |
              <button
                className="cursor-pointer border-0 bg-transparent p-0 text-right text-xs hover:underline"
                onClick={() => setIsViewList(!isViewList)}
              >
                {isViewList ? 'Hide List' : 'View As List'}
              </button>
            </span>
          )}
        </p>
      </div>
      <LocationsList
        className={cn('hidden gap-2 md:block', isViewList && 'mt-4')}
        viewAsList={isViewList}
        activeRegion={activeRegion}
        regions={regions}
      />
    </>
  );
};
type RegionListTriggerProps = {
  selectedLocation: string;
} & RegionListDrawerTriggerProps;

const RegionListTrigger = ({ selectedLocation, ...rest }: RegionListTriggerProps) => {
  return (
    <RegionListDrawerTrigger {...rest}>
      Showing: <span className="ml-0.5 font-normal">{selectedLocation}</span>
    </RegionListDrawerTrigger>
  );
};

type LocationsDropdownProps = {
  onClickAllLocation: () => void;
  onClickOption: (name: string) => void;
  onLocationSelect: () => void;
  regions: Record<string, Location[]>;
  regionNames: string[];
  selectedLocation: string;
};

const LocationsDropdown = ({
  onClickAllLocation,
  onClickOption,
  onLocationSelect,
  regions,
  regionNames,
  selectedLocation,
}: LocationsDropdownProps) => {
  const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;

    value === 'all' ? onClickAllLocation() : onClickOption(value);
    onLocationSelect();
  };

  return (
    <div className="relative">
      {/* it uses RegionListTrigger (as div so it can't be tabbed) and native select to mimic custom built select with
      computed selected value as there's no reliable way to trigger native select dropdown programmaticaly and it'd
      be a bad UX to modify selected value onChange via JS, reset it and so on */}
      <RegionListTrigger
        as="div"
        className="pointer-events-none w-full"
        chevronDirection="down"
        selectedLocation={selectedLocation}
      />

      <select
        className="absolute left-[1%] top-0 size-full cursor-pointer opacity-0"
        onChange={onChange}
        aria-label="Location options"
      >
        <option value="all">All Locations</option>

        {regionNames.map((region) => {
          const regionCount = (regions[region] || []).length;

          return (
            <option key={region} value={region}>
              {region} ({regionCount} {!!regionCount && pluralizeLocation(regionCount)})
            </option>
          );
        })}
      </select>
    </div>
  );
};

const pluralizeLocation = (count: number) => (count > 1 ? 'locations' : 'location');
